<template>
  <AccountLayout active-menu="orders">
    <div class="orders-page" v-loading="loading">
      <h1>Orders</h1>
      <div class="filters">
        <span>Show orders</span>
        <button :class="{ active: activeFilter === 'all' }" type="button" @click="changeFilter('all')">All</button>
        <button :class="{ active: activeFilter === 'active' }" type="button"
          @click="changeFilter('active')">Active</button>
        <button :class="{ active: activeFilter === 'inactive' }" type="button"
          @click="changeFilter('inactive')">Inactive</button>
      </div>
      <section v-for="(order, index) in orders" :key="index" class="order-card">
        <div class="order-meta">
          <strong>{{ order.order_no }}</strong>
          <span>{{ order.formatted_date }}</span>
          <em>{{ order.status_text_en }}</em>
          <div v-if="order.steps != undefined" class="order-steps">
            <span v-for="(step, stepIndex) in steps" :key="step" :class="{ done: stepIndex <= order.steps }">
              <i></i>{{ step }}
            </span>
          </div>
        </div>
        <table class="order-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Price, USD</th>
              <th>Sub-total, USD</th>
              <th>Status</th>
              <th>Submit Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, itemIndex) in order.product_items" :key="itemIndex">
              <td>{{ item.name }}</td>
              <td>{{ item.num }}</td>
              <td>{{ item.price }}</td>
              <td>{{ item.subtotal }}</td>
              <td v-if="itemIndex === 0" :rowspan="order.product_items.length">{{ order.status_text_en }}</td>
              <td v-if="itemIndex === 0" :rowspan="order.product_items.length">{{ order.created_at }}</td>
            </tr>
            <tr>
              <td>Sub-total: ${{ order.subtotal }}</td>
              <td colspan="5"></td>
            </tr>
          </tbody>
        </table>
        <div class="order-footer">
          <span>{{ order.product_items.length }} items in total</span>
          <span>Total amount&nbsp; ${{ order.total }}</span>
          <div class="order-actions">
            <button v-if="order.cancel" type="button" @click="cancelOrder(order)">Cancel</button>
            <button type="button" @click="openDetail(order)">View Details</button>
            <button v-if="order.primary" class="primary" type="button" @click="handlePrimaryAction(order)">{{
              order.primary }}</button>
          </div>
        </div>
      </section>
      <div class="pagination">
        <span class="muted">共 {{ total }} 条</span>
        <span class="muted">每页</span>
        <select v-model.number="pageSize" @change="onPageSizeChange">
          <option :value="10">10</option>
        </select>
        <span class="muted">条</span>
        <span class="muted" @click="prevPage">&lt;</span>
        <span>{{ currentPage }}</span>
        <span class="muted" @click="nextPage">&gt;</span>
        <span class="muted">前往</span>
        <el-input v-model="jumpPageNum" class="jump-input" @change="onJumpPage" />
        <span class="muted">页</span>
      </div>
    </div>
  </AccountLayout>
</template>

<script>
import AccountLayout from '@/components/common/AccountLayout.vue'
import { getToken } from '@/util/token-util.js'

export default {
  name: 'OrdersPage',
  components: {
    AccountLayout
  },
  data() {
    return {
      steps: ['Unpaid', 'Waiting Shipping Fee', 'Pay shipping fee', 'Waiting for dispatch', 'Pending receipt', 'Completed'],
      orders: [],
      loading: false,
      activeFilter: 'all',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      jumpPageNum: ''
    }
  },
  async mounted() {
    if (!getToken()) {
      this.$message.warning('Please log in first')
      this.$router.push({ name: 'home' })
      return
    }
    await this.loadOrders()
  },
  methods: {
    async loadOrders() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        }
        if (this.activeFilter !== 'all') {
          params.status = this.activeFilter
        }
        const res = await this.$api({ url: 'Order/list', method: 'get', data: params })
        if (res.code === 200) {
          const data = res.data
          const list = Array.isArray(data) ? data : (data.items || [])
          this.total = Array.isArray(data) ? list.length : (data.total || data.total_count || list.length)
          this.orders = list.map(item => ({
            ...item,
            steps: this.getStepIndex(item.status),
            cancel: this.canCancel(item.status),
            primary: this.getPrimaryAction(item.status),
          }))
        }
      } catch (e) {
        this.$message.error('Failed to load orders')
      } finally {
        this.loading = false
      }
    },
    getStepIndex(status) {
      const map = { 2: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5 }
      return map[status]
    },
    canCancel(status) {
      return status == 1
    },
    getPrimaryAction(status) {
      if (status === 1) return 'Pending review'
      if (status === 2) return 'Pay now'
      if (status === 4) return 'Pay shipping fee'
      if (status === 6) return 'Confirm Receipt'
      return null
    },
    changeFilter(filter) {
      this.activeFilter = filter
      this.currentPage = 1
      this.loadOrders()
    },
    openDetail(order) {
      this.$router.push({
        name: 'order-detail',
        query: { id: order.id }
      })
    },
    handlePrimaryAction(order) {
      if (order.primary === 'Confirm Receipt') {
        this.confirmReceipt(order)
      } else {
        this.openDetail(order)
      }
    },
    cancelOrder(order) {
      this.$confirm('Are you sure to cancel this order?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$api({
            url: 'Order/cancel',
            method: 'post',
            data: { id: order.id }
          })
          if (res.code === 200) {
            this.$message.success('Order cancelled')
            await this.loadOrders()
          } else {
            this.$message.error(res.message || 'Failed to cancel order')
          }
        } catch (e) {
          this.$message.error('Failed to cancel order')
        }
      }).catch(() => { })
    },
    confirmReceipt(order) {
      this.$confirm('Are you sure to confirm receipt of this order?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$api({
            url: 'Order/confirmReceipt',
            method: 'post',
            data: { id: order.id }
          })
          if (res.code === 200) {
            this.$message.success('Receipt confirmed')
            await this.loadOrders()
          } else {
            this.$message.error(res.message || 'Failed to confirm receipt')
          }
        } catch (e) {
          this.$message.error('Failed to confirm receipt')
        }
      }).catch(() => { })
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadOrders()
      }
    },
    nextPage() {
      if (this.currentPage * this.pageSize < this.total) {
        this.currentPage++
        this.loadOrders()
      }
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.loadOrders()
    },
    onJumpPage() {
      const page = parseInt(this.jumpPageNum)
      if (page && page > 0) {
        const maxPage = Math.ceil(this.total / this.pageSize)
        this.currentPage = Math.min(page, maxPage)
        this.loadOrders()
      }
    }
  }
}
</script>

<style scoped>
.orders-page h1 {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 400;
}

.filters {
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 18px;
  color: #555;
  font-size: 13px;
}

.filters button {
  height: 30px;
  padding: 0 22px;
  border: 0;
  border-radius: 15px;
  color: #777;
  background: #f3f3f3;
  cursor: pointer;
}

.filters button.active {
  border: 1px solid #F34B01;
  color: #F34B01;
  background: #fff;
}

.order-card {
  margin-bottom: 44px;
}

.order-meta {
  min-height: 28px;
  display: flex;
  align-items: center;
  gap: 28px;
  color: #777;
  font-size: 13px;
}

.order-meta strong {
  color: #F34B01;
  font-style: normal;
}

.order-meta em {
  color: #D10000;
  font-style: normal;
}

.order-steps {
  margin-left: auto;
  width: 450px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  margin-bottom: 15px;
}

.order-steps span {
  position: relative;
  color: #aaa;
  text-align: center;
  font-size: 11px;
}

.order-steps span::before {
  content: '';
  position: absolute;
  top: 7px;
  left: -50%;
  right: 50%;
  height: 3px;
  background: #bbb;
  z-index: 0;
}

.order-steps span:first-child::before {
  display: none;
}

.order-steps i {
  position: relative;
  z-index: 1;
  width: 14px;
  height: 14px;
  margin: 0 auto 4px;
  display: block;
  border-radius: 50%;
  background: #bbb;
}

.order-steps span.done,
.order-steps span.done i {
  color: #F34B01;
}

.order-steps span.done i,
.order-steps span.done::before {
  background: #F34B01;
}

.order-table {
  width: 100%;
  border: 1px solid #e2e2e2;
  border-radius: 7px 7px 0 0;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  font-size: 12px;
}

.order-table th,
.order-table td {
  height: 50px;
  border-right: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  padding: 0 18px;
  text-align: left;
}

.order-table th {
  background: #fafafa;
  font-weight: 400;
}

.order-table td:first-child {
  color: #F34B01;
}

.order-table tr:last-child td:first-child {
  color: inherit;
}

.order-table td:last-child,
.order-table th:last-child {
  /* border-right: 0; */
}

.order-table td:first-child {
  border-left: 0;
}

.order-footer {
  height: 54px;
  padding: 0 18px;
  border: 1px solid #e2e2e2;
  border-top: 0;
  border-radius: 0 0 7px 7px;
  display: flex;
  align-items: center;
  gap: 28px;
  font-size: 12px;
}

.order-actions {
  margin-left: auto;
  display: flex;
  gap: 12px;
}

.order-actions button {
  width: 92px;
  height: 30px;
  border: 1px solid #F34B01;
  border-radius: 5px;
  background: #fff;
  color: #F34B01;
  cursor: pointer;
  font-size: 12px;
}

.order-actions button.primary {
  width: 96px;
  background: #F34B01;
  color: #fff;
}

.pagination {
  margin: 36px 0 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: #999;
  font-size: 12px;
}

.pagination span {
  color: #F34B01;
}

.pagination .muted {
  color: #999;
}

.pagination select {
  width: 42px;
  height: 22px;
  border: 1px solid #ddd;
  color: #999;
  text-align: center;
}

.pagination .jump-input {
  width: 42px;
}

.pagination .jump-input ::v-deep .el-input__inner {
  height: 22px;
  border: 1px solid #ddd;
  color: #999;
  text-align: center;
  padding: 0;
}
</style>
